import { format, isValid } from 'date-fns';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MainTable from '../../components/base/MainTable';
import TruncatedText from '../../components/base/TruncatedText';
import Checkbox from '../../components/ui/Checkbox';
import SearchResult from '../../components/ui/SearchResult';
import Tooltip from '../../components/ui/Tooltip';
import { getCardValue, getPrice } from '../../utils/values';
import AddedOn from './AddedOn';
import Contact from './Contact';
import ContactPills from './ContactPills';
import styles from './Contacts.module.scss';
import { useContactsParams } from './useContactsParams';

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    return getCardValue(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }

  if (typeof value === 'string') {
    if (isValid(new Date(value))) {
      return format(new Date(value), 'eeee, MMM dd, yyyy');
    }

    return value;
  }

  return String(value);
};

const FIELDS_TO_IGNORE_IN_COLS = [
  'alid',
  'id',
  'segments',
  'lists',
  'flows',
  'port_value',
  'buying_power',
];

const COMMON_COLUMN_ORDER = [
  'checkbox',
  'contact',
  'audiences',
  'flows',
  'portfolio',
  'buying_power',
];

const ContactList = ({
  data,
  isLoading,
  selectedContacts,
  onContactSelect,
  onSelectAll,
  onRemoveList,
}) => {
  const scrollPositionRef = useRef(0);
  const { params, fieldArray } = useContactsParams();

  const [columns, setColumns] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [count, setCount] = useState(null);
  const [expandedAudienceRowId, setExpandedAudienceRowId] = useState(null);
  const [expandedFlowRowId, setExpandedFlowRowId] = useState(null);

  useLayoutEffect(() => {
    const htmlElement = document.documentElement;
    const originalScrollBehavior = htmlElement.style.scrollBehavior;
    htmlElement.style.scrollBehavior = 'auto';

    window.scrollTo({
      top: scrollPositionRef.current,
      behavior: 'auto',
    });

    setTimeout(() => {
      htmlElement.style.scrollBehavior = originalScrollBehavior;
    }, 0);
  }, [selectedContacts]);

  const checkAllContacts = (checked) => {
    scrollPositionRef.current = window.scrollY;
    onSelectAll(checked);
  };

  const handleContactSelection = (contactId, isChecked) => {
    scrollPositionRef.current = window.scrollY;
    onContactSelect(contactId, isChecked);
  };

  const totalSelectableCount = useMemo(() => {
    if (!data?.results) {
      return 0;
    }

    return data.results.filter((contact) => contact.address).length;
  }, [data?.results]);

  useEffect(() => {
    if (isLoading || !data?.results?.length) {
      setColumns([]);
      setProcessedData([]);
      setCount(null);
      setExpandedAudienceRowId(null);
      setExpandedFlowRowId(null);
      return;
    }

    setCount(data.count);

    const columnConfigMap = new Map();

    columnConfigMap.set('checkbox', {
      key: 'checkbox',
      header: (
        <Checkbox
          checked={totalSelectableCount === selectedContacts.length}
          onChange={(checked) => checkAllContacts(checked)}
        />
      ),
      renderCell: (row) => {
        if (row.address === null) {
          return (
            <>
              <Checkbox
                checked={selectedContacts.some((sc) => sc.id === row.id)}
                onChange={(isChecked) => handleContactSelection(row, isChecked)}
                disabled
                tooltipId={`tooltip-${row.id}`}
              />
              <Tooltip
                id={`tooltip-${row.id}`}
                info="This contact cannot be selected because it has no wallet address."
              />
            </>
          );
        }
        return (
          <Checkbox
            checked={selectedContacts.some((sc) => sc.id === row.id)}
            onChange={(isChecked) => handleContactSelection(row, isChecked)}
          />
        );
      },
      width: 48,
    });
    columnConfigMap.set('contact', {
      key: 'contact',
      header: 'Alias & Channels',
      renderCell: (row) => <Contact data={row} />,
    });
    columnConfigMap.set('audiences', {
      key: 'audiences',
      header: 'Audiences',
      renderCell: (row) => (
        <div className={styles.pill_cell}>
          <ContactPills
            data={row.audiences}
            rowId={row.id}
            address={row.address}
            expandedRowId={expandedAudienceRowId}
            onExpand={setExpandedAudienceRowId}
            onRemoveList={onRemoveList}
          />
        </div>
      ),
    });
    columnConfigMap.set('flows', {
      key: 'flows',
      header: 'Flows',
      renderCell: (row) => (
        <div className={styles.pill_cell}>
          <ContactPills
            data={row.flows}
            rowId={row.id}
            expandedRowId={expandedFlowRowId}
            onExpand={setExpandedFlowRowId}
          />
        </div>
      ),
    });
    columnConfigMap.set('portfolio', {
      key: 'portfolio',
      header: 'Portfolio',
      renderCell: (row) => <div>{getPrice(row.port_value)}</div>,
    });
    columnConfigMap.set('buying_power', {
      key: 'buying_power',
      header: 'Buying Power',
      renderCell: (row) => <div>{getPrice(row.buying_power)}</div>,
    });
    columnConfigMap.set('added_on', {
      key: 'added_on',
      header: 'Added On',
      renderCell: (row) => <AddedOn data={row} />,
      fieldForSorting: 'inserted_timestamp',
    });

    const dynamicColumns = fieldArray
      .filter((item) => !FIELDS_TO_IGNORE_IN_COLS.includes(item))
      .sort();
    dynamicColumns.forEach((key) => {
      columnConfigMap.set(key, {
        key,
        header: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        renderCell: (row) => <TruncatedText text={formatCellValue(row[key])} />,
        fieldForSorting: key,
      });
    });

    const commonKeys = ['checkbox', 'contact'];

    fieldArray.forEach((key) => {
      if (
        (key === 'lists' || key === 'segments') &&
        !commonKeys.includes('audiences')
      ) {
        commonKeys.push('audiences');
      } else if (key === 'port_value') {
        commonKeys.push('portfolio');
      } else if (COMMON_COLUMN_ORDER.includes(key)) {
        commonKeys.push(key);
      }
    });

    commonKeys.sort(
      (a, b) => COMMON_COLUMN_ORDER.indexOf(a) - COMMON_COLUMN_ORDER.indexOf(b)
    );

    const finalOrderedKeys = [...commonKeys, ...dynamicColumns, 'added_on'];
    const finalColumns = finalOrderedKeys.map((key) =>
      columnConfigMap.get(key)
    );

    setColumns(finalColumns);

    const finalProcessedData = data.results.map((contact) => {
      const { alid, segments = [], lists = [], flows = [], ...rest } = contact;
      return {
        ...rest,
        id: alid,
        audiences: [
          ...segments.map((item) => ({ ...item, type: 'segment' })),
          ...lists.map((item) => ({ ...item, type: 'list' })),
        ],
        flows: [...flows.map((item) => ({ ...item, type: 'flow' }))],
      };
    });

    setProcessedData(finalProcessedData);
  }, [
    data,
    selectedContacts,
    isLoading,
    expandedAudienceRowId,
    expandedFlowRowId,
  ]);

  return (
    <div>
      <div className="w-100">
        <MainTable
          total={count}
          desc={params.sort === 'desc'}
          ordering={params.orderBy || ''}
          page={params.page || 1}
          limit={params.limit || 10}
          headers={columns.map((col) => ({
            title: col.header,
            width: col.width,
            field: col.fieldForSorting,
          }))}
          data={processedData.map((row) => {
            const rowData = {};
            columns.forEach((col) => {
              rowData[col.key] = col.renderCell(row);
            });
            return rowData;
          })}
          isLoading={isLoading}
          min={10}
          nothingComponent={
            <SearchResult type="contact" search={params?.search || ''} />
          }
          scrollable
          columnMaxWidth={10}
        />
      </div>
    </div>
  );
};

export default ContactList;
